import { FormEvent, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 125, 150];

type AssignAgentRow = {
  id: number;
  userName: string;
  assignAgentSettings: string;
  mobileNumber: string;
  depoMobileNumber: string;
  firstBonusStatus: string;
};

const AssignAgentsPage = () => {
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");

  // Keeping empty table state to match the provided HTML.
  const rows: AssignAgentRow[] = [];

  const filteredList = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [
        String(row.id),
        row.userName,
        row.assignAgentSettings,
        row.mobileNumber,
        row.depoMobileNumber,
        row.firstBonusStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rows, searchText]);

  const handleDownloadCsv = (event: FormEvent) => {
    event.preventDefault();
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Assign Agent List</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="mb-3">
                <div className="row row5" />
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <div id="tickets-table_length" className="dataTables_length">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Show&nbsp;
                      <select
                        className="custom-select custom-select-sm"
                        id="__BVID__611"
                        value={pageSize}
                        onChange={(event) => setPageSize(Number(event.target.value))}
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      &nbsp;entries
                    </label>
                  </div>
                </div>

                <div className="col-sm-12 col-md-6">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Search:
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control form-control-sm ml-2 form-control"
                        id="__BVID__612"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="table-responsive mb-0">
                <div className="table no-footer table-responsive-sm">
                  <table
                    id="eventsListTbl"
                    role="table"
                    aria-busy="false"
                    aria-colcount={6}
                    className="table b-table table-bordered"
                  >
                    <thead>
                      <tr>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={1} aria-sort="none" className="position-relative">
                          <div>S.No.</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={2} aria-sort="none" className="position-relative">
                          <div>User Name</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={3} aria-sort="none" className="position-relative">
                          <div>Assign Agent Settings</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={4} aria-sort="none" className="position-relative">
                          <div>Mobile Number</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={5} aria-sort="none" className="position-relative">
                          <div>Depo Mobile Number</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                        <th role="columnheader" scope="col" tabIndex={0} aria-colindex={6} aria-sort="none" className="position-relative">
                          <div>First Bonus Status</div>
                          <span className="sr-only"> (Click to sort ascending)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredList.length === 0 ? (
                        <tr className="b-table-empty-row">
                          <td colSpan={6}>
                            <div role="alert" aria-live="polite">
                              <div className="text-center my-2">There are no records to show</div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredList.slice(0, pageSize).map((row, index) => (
                          <tr key={`${row.id}-${index}`}>
                            <td>{row.id}</td>
                            <td>{row.userName}</td>
                            <td>{row.assignAgentSettings}</td>
                            <td>{row.mobileNumber}</td>
                            <td>{row.depoMobileNumber}</td>
                            <td>{row.firstBonusStatus}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">User Creation</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form method="post" data-vv-scope="creation" className="ajaxFormSubmit" onSubmit={handleDownloadCsv}>
                <div className="row row5">
                  <div className="col-md-2">
                    <label htmlFor="assign-agent-from-date">From Date:</label>
                    <div className="mx-datepicker">
                      <div className="mx-input-wrapper">
                        <input
                          id="assign-agent-from-date"
                          name="from_date"
                          type="text"
                          autoComplete="off"
                          readOnly
                          placeholder=""
                          className="mx-input"
                        />
                        <i className="mx-icon-calendar">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
                            <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z" />
                          </svg>
                        </i>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-2">
                    <label htmlFor="assign-agent-to-date">To Date:</label>
                    <div className="mx-datepicker disabled">
                      <div className="mx-input-wrapper">
                        <input
                          id="assign-agent-to-date"
                          name="to_date"
                          type="text"
                          autoComplete="off"
                          readOnly
                          disabled
                          placeholder=""
                          className="mx-input"
                        />
                        <i className="mx-icon-calendar">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
                            <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z" />
                          </svg>
                        </i>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-2 mt-4">
                    <button type="submit" id="loaddata" className="btn btn-primary">
                      Download CSV
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignAgentsPage;
